package com.schoolconnect.app.feature.notices

import com.schoolconnect.app.datasource.network.response.ParentNoticeListItemResponse
import com.schoolconnect.app.feature.attendance.ParentAttendanceChild
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ParentChildSelectionStore
import com.schoolconnect.app.feature.common.pageCountForTotal
import com.schoolconnect.app.repository.parentnotices.ParentNoticesRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.json.*

class ParentNoticesViewModel(initialChildren: List<ParentAttendanceChild> = emptyList()) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val _uiState = MutableStateFlow(ParentNoticesUiState(children = initialChildren))
    val uiState: StateFlow<ParentNoticesUiState> = _uiState.asStateFlow()
    private var listJob: Job? = null
    private var summaryJob: Job? = null

    init {
        observeGlobalSelection()
        loadPage()
        _uiState.value.selectedChild?.id?.let(::loadSummary)
    }

    private fun observeGlobalSelection() {
        scope.launch {
            ParentChildSelectionStore.selectedChildId.collect { childId ->
                val index = _uiState.value.children.indexOfFirst { it.id == childId }
                if (index >= 0 && index != _uiState.value.selectedChildIndex) {
                    onEvent(ParentNoticesUiEvent.ChildSelected(index))
                }
            }
        }
    }

    fun onEvent(event: ParentNoticesUiEvent) {
        when (event) {
            is ParentNoticesUiEvent.ChildSelected -> {
                _uiState.update {
                    it.copy(
                        selectedChildIndex = event.index.coerceIn(0, it.children.lastIndex.coerceAtLeast(0)),
                        pageIndex = 0
                    )
                }
                loadPage()
                _uiState.value.selectedChild?.id?.let(::loadSummary)
            }
            is ParentNoticesUiEvent.SearchChanged -> {
                _uiState.update { it.copy(searchQuery = event.value, pageIndex = 0) }
                loadPage(debounce = true)
            }
            is ParentNoticesUiEvent.PeriodChanged -> {
                _uiState.update { current ->
                    current.copy(
                        period = event.value,
                        periodFrom = if (event.value.equals("Custom Range", ignoreCase = true)) current.periodFrom else "",
                        periodTo = if (event.value.equals("Custom Range", ignoreCase = true)) current.periodTo else "",
                        pageIndex = 0
                    )
                }
                loadPage()
            }
            is ParentNoticesUiEvent.CustomPeriodApplied -> {
                _uiState.update {
                    it.copy(period = "Custom Range", periodFrom = event.from, periodTo = event.to, pageIndex = 0)
                }
                loadPage()
            }
            is ParentNoticesUiEvent.StatusChanged -> {
                _uiState.update { it.copy(status = event.value, pageIndex = 0) }
                loadPage()
            }
            ParentNoticesUiEvent.FiltersCleared -> {
                _uiState.update {
                    it.copy(
                        searchQuery = "",
                        period = "All",
                        periodFrom = "",
                        periodTo = "",
                        status = "All",
                        pageIndex = 0
                    )
                }
                loadPage()
            }
            is ParentNoticesUiEvent.PageChanged -> {
                _uiState.update { it.copy(pageIndex = event.index.coerceAtLeast(0)) }
                loadPage()
            }
            is ParentNoticesUiEvent.PageSizeChanged -> {
                _uiState.update {
                    it.copy(
                        pageSize = event.size.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()),
                        pageIndex = 0
                    )
                }
                loadPage()
            }
            is ParentNoticesUiEvent.SortChanged -> {
                _uiState.update { current ->
                    current.copy(
                        sortField = event.field,
                        sortAscending = if (current.sortField == event.field) !current.sortAscending else true,
                        pageIndex = 0
                    )
                }
                loadPage()
            }
            is ParentNoticesUiEvent.ViewRequested -> loadDetail(event.row)
            ParentNoticesUiEvent.ViewDismissed -> _uiState.update {
                it.copy(viewingNotice = null, viewingDetail = null, isViewingLoading = false, viewingError = "")
            }
        }
    }

    private fun loadPage(debounce: Boolean = false) {
        listJob?.cancel()
        val studentId = _uiState.value.selectedChild?.id?.trim()?.takeIf { it.isNotBlank() }
        if (studentId == null) {
            _uiState.update {
                it.copy(rows = emptyList(), matchingCount = 0, isLoading = false, errorMessage = "No linked child was found.")
            }
            return
        }

        _uiState.update { it.copy(isLoading = true, errorMessage = "") }
        listJob = scope.launch {
            if (debounce) delay(300)
            val requested = _uiState.value
            val (publishedFrom, publishedTo) = NoticeFilters(
                publishedDate = requested.period,
                publishedDateFrom = requested.periodFrom,
                publishedDateTo = requested.periodTo
            ).toApiPublishedRange()
            ParentNoticesRepository.loadPage(
                studentId = studentId,
                search = requested.searchQuery.trim().takeIf { it.isNotBlank() },
                status = requested.status.toApiParentNoticeStatus(),
                publishedFrom = publishedFrom,
                publishedTo = publishedTo,
                pageIndex = requested.pageIndex,
                pageSize = requested.pageSize,
                sortBy = requested.sortField.toParentNoticeSortField(),
                order = if (requested.sortAscending) "asc" else "desc"
            ).onSuccess { result ->
                val lastPageIndex = (pageCountForTotal(result.totalCount, requested.pageSize) - 1).coerceAtLeast(0)
                if (requested.pageIndex > lastPageIndex) {
                    _uiState.update { current ->
                        if (current.selectedChild?.id == studentId) current.copy(pageIndex = lastPageIndex) else current
                    }
                    loadPage()
                    return@launch
                }
                _uiState.update { current ->
                    if (current.selectedChild?.id != studentId) current
                    else current.copy(
                        rows = result.items.map(ParentNoticeListItemResponse::toRow),
                        matchingCount = result.totalCount,
                        isLoading = false,
                        errorMessage = ""
                    )
                }
            }.onFailure { error ->
                _uiState.update {
                    it.copy(isLoading = false, errorMessage = error.message ?: "Unable to load notices.")
                }
            }
        }
    }

    private fun loadSummary(studentId: String) {
        summaryJob?.cancel()
        summaryJob = scope.launch {
            ParentNoticesRepository.loadSummary(studentId).onSuccess { raw ->
                if (_uiState.value.selectedChild?.id == studentId) applySummary(raw)
            }
        }
    }

    private fun applySummary(raw: JsonObject) {
        val summary = raw.firstObject("notice_counters", "statistics", "summary", "counters") ?: raw
        _uiState.update {
            it.copy(
                totalCount = summary.firstInt("total", "total_notices") ?: it.totalCount,
                activeCount = summary.firstInt("active", "active_notices") ?: it.rows.count { row -> row.status == "Active" },
                expiredCount = summary.firstInt("expired", "expired_notices") ?: it.rows.count { row -> row.status == "Expired" },
                todayCount = summary.firstInt("today", "today_notices") ?: 0
            )
        }
    }

    private fun loadDetail(row: NoticeRowUi) {
        _uiState.update { it.copy(viewingNotice = row, viewingDetail = null, isViewingLoading = true, viewingError = "") }
        scope.launch {
            ParentNoticesRepository.loadDetail(row.backendId.ifBlank { row.id })
                .onSuccess { raw ->
                    val detail = raw.toDetail(row)
                    val attachments = ParentNoticesRepository.loadAttachmentDetails(
                        detail.attachmentFileIds,
                        detail.attachmentFileUrls
                    ).map { attachment ->
                        NoticeAttachmentDetailUi(
                            fileId = attachment.fileId,
                            url = attachment.url,
                            name = attachment.name,
                            mimeType = attachment.mimeType,
                            sizeLabel = attachment.sizeLabel,
                            previewBytes = attachment.previewBytes
                        )
                    }
                    _uiState.update {
                        if (it.viewingNotice?.backendId == row.backendId) it.copy(viewingDetail = detail.copy(attachments = attachments), isViewingLoading = false)
                        else it
                    }
                }
                .onFailure { error -> _uiState.update { it.copy(isViewingLoading = false, viewingError = error.message ?: "Unable to load notice details.") } }
        }
    }

    fun clear() {
        listJob?.cancel()
        summaryJob?.cancel()
        scope.cancel()
    }
}

private fun String.toApiParentNoticeStatus(): String? = when (trim().lowercase()) {
    "active" -> "ACTIVE"
    "expired" -> "EXPIRED"
    else -> null
}

private fun NoticesSortField.toParentNoticeSortField(): String = when (this) {
    NoticesSortField.ID -> "notice_id"
    NoticesSortField.TITLE -> "title"
    NoticesSortField.AUDIENCE -> "audience"
    NoticesSortField.PUBLISHED_DATE -> "created_at"
    NoticesSortField.EXPIRY_DATE -> "expires_at"
    NoticesSortField.STATUS -> "status"
}

private fun ParentNoticeListItemResponse.toRow(): NoticeRowUi {
    val stableId = id.orEmpty().ifBlank { noticeId.orEmpty() }
    return NoticeRowUi(
        id = stableId,
        backendId = stableId,
        title = title.orEmpty().ifBlank { "—" },
        audience = applicableTo.orEmpty().ifBlank { "—" },
        publishedOn = publishedOn.orEmpty().toDisplayDate(),
        expiryDate = expiryDate.orEmpty().toDisplayDate(),
        status = status.orEmpty().ifBlank { "Active" }.toDisplayStatus()
    )
}

private fun JsonObject.toDetail(fallback: NoticeRowUi): NoticeDetailUi {
    val createdAt = firstString("published_on", "published_at", "created_at") ?: fallback.publishedOn
    val updatedAt = firstString("updated_at")?.toDisplayTimestamp() ?: "—"
    return NoticeDetailUi(
        id = firstString("id", "notice_id") ?: fallback.id,
        title = firstString("title") ?: fallback.title,
        content = firstString("description", "content", "body") ?: "—",
        audience = firstString("applicable_to", "audience") ?: fallback.audience,
        expiryDate = (firstString("expiry_date", "expires_at") ?: fallback.expiryDate).toDisplayDate(),
        status = (firstString("status") ?: fallback.status).toDisplayStatus(),
        createdAt = createdAt.toDisplayTimestamp(),
        updatedAt = updatedAt,
        attachmentFileIds = firstStringList("attachment_file_ids", "file_ids") ?: emptyList(),
        attachmentFileUrls = firstStringList("attachment_file_urls", "file_urls") ?: emptyList(),
        createdBy = firstString("published_by", "created_by_name", "created_by") ?: "Not available",
        updatedBy = firstString("updated_by_name", "updated_by") ?: "Not available"
    )
}
private fun JsonObject.firstString(vararg keys: String): String? = keys.firstNotNullOfOrNull { key ->
    val value = get(key)
    runCatching { value?.jsonPrimitive?.contentOrNull }.getOrNull()?.takeIf(String::isNotBlank)
        ?: runCatching { value?.jsonObject?.firstString("name", "full_name", "display_name") }.getOrNull()
}
private fun JsonObject.firstInt(vararg keys: String): Int? = firstString(*keys)?.toIntOrNull()
private fun JsonObject.firstObject(vararg keys: String): JsonObject? = keys.firstNotNullOfOrNull { get(it)?.jsonObject }
private fun JsonObject.firstStringList(vararg keys: String): List<String>? = keys.firstNotNullOfOrNull { get(it)?.jsonArray?.mapNotNull { value -> value.jsonPrimitive.contentOrNull } }
private fun String.toDisplayDate(): String {
    val value = trim()
    if (value.isBlank()) return "—"
    val parts = value.take(10).split("-")
    if (parts.size != 3) return value
    val month = parts[1].toIntOrNull()?.minus(1) ?: return value
    val monthLabel = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec").getOrNull(month) ?: return value
    return "${parts[2]} $monthLabel ${parts[0]}"
}
private fun String.toDisplayTimestamp(): String {
    val value = trim()
    if (value.isBlank()) return "—"
    val local = runCatching {
        Instant.parse(value).toLocalDateTime(TimeZone.currentSystemDefault())
    }.getOrElse {
        runCatching {
            LocalDateTime.parse(value).toInstant(TimeZone.UTC)
                .toLocalDateTime(TimeZone.currentSystemDefault())
        }.getOrNull()
    } ?: return value.substringBefore('T').toDisplayDate()

    val month = listOf("JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC")
        .getOrNull(local.month.ordinal) ?: return value.substringBefore('T').toDisplayDate()
    val hour = (local.hour % 12).let { if (it == 0) 12 else it }
    val suffix = if (local.hour < 12) "AM" else "PM"
    return "${local.day.toString().padStart(2, '0')} $month ${local.year}, ${hour.toString().padStart(2, '0')}:${local.minute.toString().padStart(2, '0')} $suffix"
}
private fun String.toDisplayStatus(): String = when (uppercase().replace('_', ' ')) { "EXPIRED" -> "Expired"; "ACTIVE" -> "Active"; else -> replaceFirstChar { it.uppercase() } }
